import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { apiErrorMessage } from "../lib/utils";
import { ArrowRight, Mail, Lock, User } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import Logo from "../components/Logo";
import { LocaleSwitcher } from "../components/LocaleSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthPage() {
  const { t } = useTranslation();
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) { toast.error(t("auth.errors.required")); return; }
    if (mode === "signup" && !form.name) { toast.error(t("auth.errors.name")); return; }
    setLoading(true);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/signup";
      const body = mode === "login" ? { email: form.email, password: form.password } : { name: form.name, email: form.email, password: form.password };
      const res = await axios.post(`${API}${endpoint}`, body, { withCredentials: true });
      login(res.data);
      toast.success(mode === "login" ? t("auth.toasts.welcomeBack") : t("auth.toasts.created"));
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(apiErrorMessage(err, t("auth.errors.failed")));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const highlights = t("auth.highlights", { returnObjects: true });

  return (
    <div className="min-h-screen flex" style={{ background: "#111111" }}>
      {/* Left: Form */}
      <div className="flex-1 flex flex-col justify-center px-8 py-12 lg:px-14 max-w-lg">
        <div className="flex items-center justify-between mb-10">
          <Link to="/" className="flex items-center" data-testid="auth-logo-link">
            <Logo size="lg" />
          </Link>
          <LocaleSwitcher showCurrency={false} />
        </div>

        <motion.div key={mode} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25 }}>
          <h1 className="text-3xl font-black text-white mb-2" style={{ fontFamily: "Epilogue, sans-serif" }}>
            {mode === "login" ? t("auth.loginTitle") : t("auth.signupTitle")}
          </h1>
          <p className="text-sm mb-8" style={{ color: "#6b6b6b" }}>
            {mode === "login" ? t("auth.loginSubtitle") : t("auth.signupSubtitle")}
          </p>

          {/* Google */}
          <button type="button" onClick={handleGoogleLogin} data-testid="google-auth-btn"
            className="w-full h-11 flex items-center justify-center gap-2 rounded-xl text-sm font-medium transition-colors mb-6"
            style={{ background: "#1c1c1c", border: "1px solid #2d2d2d", color: "#d4d4d4" }}
            onMouseOver={e => e.currentTarget.style.background = "#222222"}
            onMouseOut={e => e.currentTarget.style.background = "#1c1c1c"}>
            <svg className="w-4 h-4" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t("auth.google")}
          </button>

          <div className="relative flex items-center my-5">
            <div className="flex-1 border-t" style={{ borderColor: "#252525" }} />
            <span className="px-3 text-xs" style={{ color: "#6b6b6b" }}>{t("common.or")}</span>
            <div className="flex-1 border-t" style={{ borderColor: "#252525" }} />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
            <AnimatePresence>
              {mode === "signup" && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
                  <label className="text-sm font-medium" style={{ color: "#a0a0a0" }}>{t("auth.fullName")}</label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#6b6b6b" }} />
                    <input placeholder={t("auth.namePlaceholder")} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                      data-testid="auth-name-input"
                      className="w-full pl-9 h-11 rounded-xl text-sm text-white placeholder:text-zinc-400 outline-none focus:ring-1 focus:ring-purple-500/50"
                      style={{ background: "#1c1c1c", border: "1px solid #2d2d2d" }} />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div>
              <label className="text-sm font-medium" style={{ color: "#a0a0a0" }}>{t("auth.email")}</label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#6b6b6b" }} />
                <input type="email" placeholder="you@company.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  data-testid="auth-email-input"
                  className="w-full pl-9 h-11 rounded-xl text-sm text-white placeholder:text-zinc-400 outline-none focus:ring-1 focus:ring-purple-500/50"
                  style={{ background: "#1c1c1c", border: "1px solid #2d2d2d" }} />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium" style={{ color: "#a0a0a0" }}>{t("auth.password")}</label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#6b6b6b" }} />
                <input type="password" placeholder="••••••••" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                  data-testid="auth-password-input"
                  className="w-full pl-9 h-11 rounded-xl text-sm text-white placeholder:text-zinc-400 outline-none focus:ring-1 focus:ring-purple-500/50"
                  style={{ background: "#1c1c1c", border: "1px solid #2d2d2d" }} />
              </div>
            </div>

            <button type="submit" disabled={loading} data-testid="auth-submit-btn"
              className="w-full h-11 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2">
              {loading ? t("auth.pleaseWait") : (mode === "login" ? t("auth.signIn") : t("auth.createAccount"))}
              {!loading && <ArrowRight className="w-4 h-4 rtl:rotate-180" />}
            </button>
          </form>

          <p className="text-center text-sm mt-6" style={{ color: "#6b6b6b" }}>
            {mode === "login" ? t("auth.noAccount") : t("auth.haveAccount")}{" "}
            <button onClick={() => setMode(mode === "login" ? "signup" : "login")} data-testid="auth-toggle-btn"
              className="text-purple-400 font-semibold hover:text-purple-300 transition-colors">
              {mode === "login" ? t("auth.signUpFree") : t("auth.signInLink")}
            </button>
          </p>
        </motion.div>
      </div>

      {/* Right: Image panel */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden" style={{ borderLeft: "1px solid #1e1e1e" }}>
        <img src="https://images.unsplash.com/photo-1632516643720-e7f5d7d6ecc9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMDNkJTIwZ3JhZGllbnQlMjBzaGFwZXN8ZW58MHx8fHwxNzcyMjI4NDgwfDA&ixlib=rb-4.1.0&q=85"
          alt="Abstract creative" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(88,28,135,0.3) 0%, rgba(17,17,17,0.7) 100%)" }} />
        <div className="absolute inset-0 flex flex-col justify-center px-12 text-white">
          <h2 className="text-3xl font-black mb-4 leading-tight" style={{ fontFamily: "Epilogue, sans-serif" }}>
            {t("auth.panelTitle")}
          </h2>
          <p className="text-base leading-relaxed mb-6" style={{ color: "#a0a0a0" }}>
            {t("auth.panelSubtitle")}
          </p>
          <div className="space-y-3">
            {highlights.map(item => (
              <div key={item} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "rgba(168,85,247,0.2)", border: "1px solid rgba(168,85,247,0.4)" }}>
                  <svg className="w-3 h-3 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-sm" style={{ color: "#a0a0a0" }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
